import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mic, MicOff, Info } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import { useMeetingLock } from '../contexts/MeetingLockContext';
import useAllowPrint from '../components/useAllowPrint';

const MIN_WORDS = 10;
const MAX_WORDS = 500;
const MAX_RECORDING_SECONDS = 300; // 5 minutes

function JournalPage() {
  useAllowPrint();
  const navigate = useNavigate();
  const { active: meetingLocked } = useMeetingLock();
  const [content, setContent] = useState('');
  const [moodScore, setMoodScore] = useState([5]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [entries, setEntries] = useState([]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const isUnderMin = wordCount < MIN_WORDS;
  const isOverMax = wordCount > MAX_WORDS;

  useEffect(() => {
    fetchEntries();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/journals`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    }
  };

  const handleSubmit = async () => {
    if (isUnderMin) {
      toast.error(`Please write at least ${MIN_WORDS} words`);
      return;
    }
    if (isOverMax) {
      toast.error(`Please keep your entry under ${MAX_WORDS} words`);
      return;
    }

    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/journals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, mood_score: moodScore[0] })
      });

      if (response.ok) {
        toast.success('Journal entry saved');
        setContent('');
        setMoodScore([5]);
        fetchEntries();
      } else {
        toast.error('Failed to save entry');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('An error occurred');
    }
  };

  const startRecording = async () => {
    if (meetingLocked) {
      toast.warning('Microphone is locked while a meeting is in progress.  Take your After selfie in the Meeting Log to release the lock.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return MAX_RECORDING_SECONDS;
          }
          return prev + 1;
        });
      }, 1000);

      toast.success('Recording started (max 5 minutes)');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    toast.info('Processing audio...');
  };

  const transcribeAudio = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/journals/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setContent(prev => prev + (prev ? ' ' : '') + data.text);
        toast.success('Transcription complete');
      } else {
        toast.error('Transcription failed');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Transcription failed');
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <PageHeader title="Daily Journal" />

      <main className="pt-20 pb-6 px-4">
        <div className="max-w-md mx-auto">
          {/* Explanatory Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4" data-testid="journal-info-banner">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800 leading-relaxed">
                <p className="font-semibold mb-1">Daily Journal Requirements</p>
                <p>Write at least <strong>{MIN_WORDS} words</strong> (max {MAX_WORDS}) or use voice recording (up to <strong>5 minutes</strong>) to transcribe your thoughts. You need 5 entries per week to progress.</p>
              </div>
            </div>
          </div>

          {/* Entry Form */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
            <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Journal Entry</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write about your day, your feelings, your progress..."
              rows={8}
              className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md mb-2"
              data-testid="journal-textarea"
            />

            {/* Word count indicator */}
            <div className="flex justify-between items-center mb-4">
              <span className={`text-xs font-medium ${isOverMax ? 'text-red-600' : isUnderMin ? 'text-amber-600' : 'text-green-600'}`} data-testid="word-count">
                {wordCount}/{MAX_WORDS} words
                {isUnderMin && ` (min ${MIN_WORDS})`}
              </span>
              <div className="h-1.5 w-24 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isOverMax ? 'bg-red-500' : isUnderMin ? 'bg-amber-400' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(100, (wordCount / MAX_WORDS) * 100)}%` }}
                />
              </div>
            </div>

            {/* Voice Recording */}
            <div className="mb-4">
              {!isRecording ? (
                <Button
                  onClick={startRecording}
                  variant="outline"
                  className="w-full h-12 border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="start-recording-btn"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Voice Entry (up to 5 min)
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button
                    onClick={stopRecording}
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="stop-recording-btn"
                  >
                    <MicOff className="w-4 h-4 mr-2" />
                    Stop Recording
                  </Button>
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs text-red-600 font-medium animate-pulse">Recording...</span>
                    <span className="text-xs font-mono text-zinc-600" data-testid="recording-timer">
                      {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_SECONDS)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 rounded-full transition-all"
                      style={{ width: `${(recordingTime / MAX_RECORDING_SECONDS) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Mood Slider */}
            <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Mood Score: {moodScore[0]}/10</Label>
            <Slider
              value={moodScore}
              onValueChange={setMoodScore}
              min={1}
              max={10}
              step={1}
              className="mb-4"
              data-testid="mood-slider"
            />
            <div className="flex justify-between text-xs text-zinc-500">
              <span>Low</span>
              <span>Stable</span>
              <span>High</span>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={isUnderMin || isOverMax}
              className="w-full mt-4 bg-zinc-950 text-white hover:bg-zinc-800 rounded-md h-12 disabled:opacity-50"
              data-testid="submit-journal-btn"
            >
              {isUnderMin ? `Write at least ${MIN_WORDS - wordCount} more word${MIN_WORDS - wordCount === 1 ? '' : 's'}` : 'Save Entry'}
            </Button>
          </div>

          {/* Recent Entries */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Recent Entries</p>
            {entries.length === 0 && (
              <p className="text-sm text-zinc-500 text-center py-4">No entries yet. Write your first journal entry above.</p>
            )}
            {entries.slice(0, 5).map((entry) => (
              <div key={entry.journal_id} className="bg-white border border-zinc-200 rounded-md p-4">
                <p className="text-sm text-zinc-700 mb-2">{entry.content.substring(0, 150)}{entry.content.length > 150 ? '...' : ''}</p>
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>Mood: {entry.mood_score}/10</span>
                  <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default JournalPage;
